import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { listConnectors } from "../../../api/connectorsApi";
import { useLoad } from "../../../shared/loading/useLoad";
import OpenOnPc from "../../connectors/pcPage/OpenOnPc";
import type { RouteNotReadyProblem } from "./routeReadiness";
import { approvalTargetOf } from "./useApprovalPage";

/**
 * Which agent, on which PC, and the fix there, in the person's language (CW-1), never the
 * server's English sentence; the PC's owner gets its own sign-in or set-up there (F122).
 */
export default function RouteNotReady({ problem }: { problem: RouteNotReadyProblem }) {
  const { t } = useTranslation();
  // The owner's stored connector report names the PC and agent even while offline.
  const load = useCallback((signal: AbortSignal) => listConnectors(signal), []);
  const { state } = useLoad(load);
  const connectors = state.status === "loaded" ? state.value : [];
  const connector = connectors.find((candidate) => candidate.connectorId === problem.connectorId);
  const pc = connector?.name ?? t("threads.toolApproval.hostPc");
  const agent = connector?.status.agents.find((candidate) => candidate.agentId === problem.agentId)?.title ?? problem.agentId;
  // Offering an action still requires the owner's live PC and its page (TA-4).
  const target = approvalTargetOf(connectors, problem.connectorId, null);
  const signIn = problem.reason === "not_logged_in";
  return (
    <>
      <span>{t(`threads.routeNotReady.${problem.reason}`, { agent, pc })}</span>
      {target === null || !target.pageServed ? null : (
        <OpenOnPc
          connectorId={target.connectorId}
          pc={target.pc}
          section="agents"
          signInAgent={signIn ? problem.agentId : undefined}
          label={t(signIn ? "connectors.providers.signInOn" : "connectors.providers.setUpOn", { pc: target.pc })}
          command={signIn ? { args: `agents login ${problem.agentId}`, aboutServer: false } : { args: "tools page", aboutServer: true }}
          variant="secondary"
        />
      )}
    </>
  );
}
